import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { HighlightProvider as SharedHighlightProvider } from "@learningblocks/shared-components";
import { useHighlight } from "@learningblocks/shared/glossary";
import { ContentDropdown } from "@learningblocks/shared-components/lib/ContentDropdown";
import { Image } from "@learningblocks/shared-components/lib/Image";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { useIntl } from "react-intl";
import cn from "./style.module.scss";

const TYPE_OBJECT = "object";

/**
 * NotebookImage:
 *  - Component to display Images in Notebook [Experimental Designs]
 *  - Display Image with/without caption
 */
export function NotebookImage(props) {
  const intl = useIntl();
  const className = `${cn.image} ${props.className} notebook-image-component`;
  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  const { onPlainTextClick } =
    React.useContext(SharedHighlightProvider.Context) || useHighlight();

  const getImageSrc = (locale) => {
    if (props.preview) {
      return props.image.url;
    }
    const src = props.image.src.replace("{{locale}}", locale);
    if (src.includes("images/")) {
      return src;
    }
    return `${baseUrl}locales/${locale}/images/${src}`;
  };

  const [imageSrc, setImageSrc] = useState(getImageSrc(intl.locale));

  // When the image load errors, set the src to use the English path
  const handleImageError = () => {
    setImageSrc(getImageSrc("en"));
  };

  // When the language changes, set the image src to use the respective language path
  useEffect(() => {
    setImageSrc(getImageSrc(intl.locale));
  }, [intl.locale]);

  const onClickHighlight = (contentId, event) => {
    onPlainTextClick(contentId, event);
  };

  if (typeof props.image === TYPE_OBJECT) {
    const caption =
      typeof props.image.caption === TYPE_OBJECT
        ? props.image.caption
        : { text: props.image.caption };
    return (
      <figure className={className}>
        <Image
          src={imageSrc}
          alt={
            props.image.altText &&
            intl.formatMessage({
              id: props.image.altText,
              defaultMessage: props.image.altText,
              description: "",
            })
          }
          onError={() => handleImageError()}
          width="100%"
        />
        {props.image.caption ? (
          <figcaption className={cn.figcaption}>
            <RichText {...caption} onClickHighlight={onClickHighlight} />
          </figcaption>
        ) : null}

        {props.image.contentDropdown && props.image.contentDropdown.content ? (
          <ContentDropdown contentDetails={props.image.contentDropdown} />
        ) : null}
      </figure>
    );
  } else {
    return (
      <>
        <Image
          width="100%"
          src={props.image}
          alt={intl.formatMessage({
            id: props.image.altText,
            defaultMessage: props.image.altText,
            description: "",
          })}
          className={className}
          tabIndex={props.tabIndex}
        />
      </>
    );
  }
}

NotebookImage.propTypes = {
  /**
   * tabIndex to set on component
   */
  tabIndex: PropTypes.number,
  /**
   *  ClassName
   */
  className: PropTypes.string,
  /**
   * Image to display
   *  - could be simply a url to image
   *  - or could be an object of type {src,caption}
   */
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      /**
       * Url to image
       */
      src: PropTypes.string,
      altText: PropTypes.string,
      /**
       * caption for image
       */
      caption: PropTypes.string,
    }),
  ]),
  /**
   * Callback for when Highlighted text is clicked,
   *  - Used in RichText componnet which is used when image
   *    is displayed with caption.
   */
  onClickHighlight: PropTypes.func,
  preview: PropTypes.bool,
};

NotebookImage.defaultProps = {
  tabIndex: 0,
  className: "",
  preview: false,
};
